/*
 * <delivery-estimate> — expected delivery date on the PDP.
 *
 * Computed in the browser rather than in Liquid because Shopify caches
 * storefront HTML, so a server-side `'now'` would go stale.
 *
 * Rules (all times Europe/Brussels):
 * - Orders placed on a working day before the cutoff ship that day;
 *   otherwise they ship on the next working day.
 * - Working day = Mon–Fri and not listed in the theme's closed dates.
 * - Delivery = ship date + N working days (no weekend delivery).
 *   Belgium gets a single date, other EU countries a min–max range.
 */
class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.cutoffHour = parseInt(this.dataset.cutoffHour, 10);
    this.minDays = parseInt(this.dataset.minDays, 10);
    this.maxDays = parseInt(this.dataset.maxDays, 10);
    this.closedDates = new Set(this.dataset.closedDates.match(/\d{4}-\d{2}-\d{2}/g) || []);
    this.output = this.querySelector('[data-delivery-estimate-date]');

    this.render();
    // Keeps the date right if the tab stays open past the cutoff or midnight.
    this.interval = setInterval(() => this.render(), 60000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  // Today's calendar date and current hour in Brussels. The date is
  // returned as a UTC-midnight Date so day arithmetic ignores DST.
  brusselsNow() {
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Brussels',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .forEach(({ type, value }) => (parts[type] = value));

    return {
      date: new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day)),
      hour: +parts.hour,
    };
  }

  isWorkingDay(date) {
    const day = date.getUTCDay();
    return day !== 0 && day !== 6 && !this.closedDates.has(date.toISOString().slice(0, 10));
  }

  nextWorkingDay(date) {
    const next = new Date(date);
    do {
      next.setUTCDate(next.getUTCDate() + 1);
    } while (!this.isWorkingDay(next));
    return next;
  }

  addWorkingDays(date, count) {
    let result = date;
    for (let i = 0; i < count; i++) result = this.nextWorkingDay(result);
    return result;
  }

  render() {
    const { date: today, hour } = this.brusselsNow();
    const shipDate =
      this.isWorkingDay(today) && hour < this.cutoffHour ? today : this.nextWorkingDay(today);

    const from = this.addWorkingDays(shipDate, this.minDays);
    const to = this.addWorkingDays(shipDate, this.maxDays);

    const format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    });

    let text;
    if (from.getTime() === to.getTime()) {
      text = format.format(from);
    } else if (typeof format.formatRange === 'function') {
      text = format.formatRange(from, to);
    } else {
      text = `${format.format(from)} – ${format.format(to)}`;
    }

    this.output.textContent = text;
    this.style.visibility = '';
  }
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
